import moment from 'moment-timezone';
import { CampaignDraft, CampaignStatus, EmailLocation, RecipientType } from './types';

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
    draft: 'Draft',
    sending: 'Sending',
    sent: 'Sent',
    failed: 'Failed'
};

// "first_responder" → "First responder", for API ids without a label here.
const humanize = (id: string): string => (id ? id.charAt(0).toUpperCase() + id.slice(1).replace(/_/g, ' ') : '');

// Gallery categories come from master data; unknown ids are shown humanized.
export const templateCategoryLabel = (category: string): string =>
    TEMPLATE_CATEGORY_LABELS[category] ?? humanize(category);

export const RECIPIENT_TYPE_LABELS: Record<RecipientType, string> = {
    all_clients: 'All clients',
    leads: 'Leads',
    all_profiles: 'All profiles',
    client_groups: 'Client groups'
};

export const TEMPLATE_CATEGORY_LABELS: Record<string, string> = {
    promotional: 'Promotional',
    seasonal: 'Seasonal',
    engagement: 'Engagement'
};

export const formatNumber = (value: number): string => value.toLocaleString('en-US');

export const formatPercent = (value: number | null, decimals = 1): string =>
    value === null ? '—' : `${(value * 100).toFixed(decimals)}%`;

export const formatDate = (value: string | null): string => (value ? moment(value).format('MMM D, YYYY') : '—');

export const formatDateTime = (value: string | null): string =>
    value ? moment(value).format('MMM D, YYYY [at] h:mm A') : '—';

export const formatRelative = (value: string): string => moment(value).fromNow();

// Use the provider's describeLocations, which passes the organization's locations.
export const describeLocations = (locationIds: Array<number>, locations: Array<EmailLocation>): string => {
    if (locationIds.length === 0) return 'No locations';
    if (locationIds.length === locations.length) return 'All locations';
    if (locationIds.length === 1)
        return locations.find(location => location.id === locationIds[0])?.name ?? '1 location';
    return `${locationIds.length} locations`;
};

export const locationNames = (locationIds: Array<number>, locations: Array<EmailLocation>): Array<string> =>
    locations.filter(location => locationIds.includes(location.id)).map(location => location.name);

// Manual picks from the Edit recipients drawer: "2 selected" (include mode) or "3 excluded" (exclude mode).
const describeSelection = (selection?: Pick<CampaignDraft, 'selectedCustomerIds' | 'excludedCustomerIds'>): string => {
    if (!selection) return '';
    if (selection.selectedCustomerIds !== 'all')
        return `${formatNumber(selection.selectedCustomerIds.length)} selected`;
    return selection.excludedCustomerIds.length ? `${formatNumber(selection.excludedCustomerIds.length)} excluded` : '';
};

export const describeAudience = (
    recipientType: RecipientType,
    tags: Array<string>,
    selection?: Pick<CampaignDraft, 'selectedCustomerIds' | 'excludedCustomerIds'>
): string => {
    let base: string = RECIPIENT_TYPE_LABELS[recipientType];
    if (recipientType === 'client_groups') {
        // Tag ids from the API (e.g. "first_responder") shown in a readable form.
        const names = tags.map(humanize);
        base = names.length ? `Clients tagged ${names.join(', ')}` : 'Client groups';
    }
    const picks = describeSelection(selection);
    return picks ? `${base} · ${picks}` : base;
};

// Inserts text at the caret of a controlled input/textarea (same approach as the marketplace wizard):
// sets the native value and dispatches `input` so React's onChange runs, then restores the caret.
export const insertAtCursor = (el: HTMLInputElement | HTMLTextAreaElement | null, text: string): void => {
    if (!el) return;

    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const nextValue = el.value.slice(0, start) + text + el.value.slice(end);

    const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    if (!setter) return;

    setter.call(el, nextValue);
    el.dispatchEvent(new Event('input', { bubbles: true }));

    requestAnimationFrame(() => {
        el.focus();
        const caret = start + text.length;
        el.setSelectionRange(caret, caret);
    });
};
